import type { AxiosError } from 'axios';

/**
 * Structured API error carrying the HTTP status and raw response payload.
 *
 * Use `instanceof ApiError` to distinguish API failures from network errors,
 * and inspect `status` / `data` for programmatic branching instead of
 * fragile string matching on the message.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Extract a human-readable error message from an API error response.
 * Handles both controller-level responses (`message`) and
 * Spring Security / GlobalExceptionHandler responses (`error` + `message`).
 */
export function extractErrorMessage(data: Record<string, unknown>, fallback: string): string {
  if (typeof data.message === 'string' && data.message) return data.message;
  if (typeof data.error === 'string' && data.error) return data.error;
  return fallback;
}

/**
 * The user-facing reason an axios request failed, or `fallback` when the
 * response carries none. Reads `message` alone: `error` holds a machine code
 * ("BadRequest"), which is worse than the caller's own wording. The body is
 * whatever the wire produced, so a non-string reaching a React child would
 * take the page down; anything but a non-empty string falls back.
 */
export function axiosErrorMessage(err: unknown, fallback: string): string {
  const ax = err as AxiosError<{ message?: unknown }> | undefined;
  const message = ax?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/** Throw an `ApiError` when the response is not ok. */
export function throwApiError(
  res: Response,
  data: Record<string, unknown>,
  fallback: string,
): never {
  throw new ApiError(extractErrorMessage(data, fallback), res.status, data);
}
